/**
 * TypeScript mirror of the worker's station_volumes.json (schema v1) and of
 * the summary-only block postprocess writes onto layer metadata.
 * Source of truth: scripts/station_bins.py :: compute_station_bins (the
 * `result` dict). Keep every key in sync with that function.
 */
import type { Criterion, Verdict } from '@/components/VerdictChip';

export const STATION_VOLUMES_SCHEMA_VERSION = 1;

export interface StationEquationMeta {
  staInternal: number;
  staAhead: number;
}

export interface StationAlignmentMeta {
  name: string;
  key: string;
  layerId: string | null;
  /**
   * The name of the layer the alignment belongs to, resolved server-side
   * (`preprocess.ts`) because the browser only ever sends the id. Absent on
   * runs made before this existed and on runs whose alignment layer was
   * deleted — the CSV's `Layer:` line falls back to `layerId`.
   */
  layerName?: string;
  sourceHash: string;
  lengthM: number;
  staStart: number;
  equations: StationEquationMeta[];
  /** Minimum circumradius over the tessellation; null when perfectly straight. */
  minRadiusM: number | null;
  /** minRadiusM < max(swathLeft, swathRight) — bin edges are approximate. */
  curvatureWarning: boolean;
}

/**
 * The CROSSFALL check (crossfall spec §4-5), mirrored from
 * `scripts/crossfall.py`'s per-side block (its `BLOCK_KEYS`, key for key).
 * Every figure is `null` — never a zero — on an INCONCLUSIVE side: a zero
 * crossfall is a flat road, which is a measurement, and this is the absence
 * of one.
 *
 * `asbuiltLine` / `designLine` are the FITTED line's endpoints at the band's
 * own `fromM` / `toM`, so every drawer (the section tab, the PDF SVG) plots
 * two points and computes nothing — the report twin rule, which forbids new
 * arithmetic in `processReport/sectionGeometry.ts`.
 */
export interface CrossfallFitLine {
  u0: number;
  z0: number;
  u1: number;
  z1: number;
}

export interface CrossfallSide {
  asbuiltPct: number | null;
  designPct: number | null;
  deltaPct: number | null;
  deltaMm: number | null;
  /** Standard error of the fitted SLOPE, in percent. `null` under three
   *  points: two points fit any line exactly. */
  sigmaPct: number | null;
  /** Standard error of Δ, combining BOTH fits in quadrature and floored at
   *  the precision the survey's own point noise can support (spec §12.1).
   *  This — not `sigmaPct` — is what decides UNCERTAIN. */
  sigmaDeltaPct?: number | null;
  /** Δ's interval, `Δ ∓ CI_K·σΔ`. A side abstains only when this spans both
   *  the pass band and the fail band. */
  ciLowPct?: number | null;
  ciHighPct?: number | null;
  residualMm: number | null;
  nValid: number;
  nBand: number;
  verdict: Verdict;
  /** Why the side is INCONCLUSIVE; `null` on every judged side. */
  reason: 'COVERAGE' | 'UNCERTAIN' | null;
  asbuiltLine: CrossfallFitLine | null;
  designLine: CrossfallFitLine | null;
}

/** Both bands of one station line. A side the run never configured is `null`. */
export interface StationCrossfall {
  left: CrossfallSide | null;
  right: CrossfallSide | null;
}

/** One band, in metres of offset from the centreline, on its own side. */
export interface CrossfallBand {
  fromM: number;
  toM: number;
}

export interface CrossfallParams {
  left: CrossfallBand | null;
  right: CrossfallBand | null;
  tolerancePct: number;
}

export interface CrossfallSummary {
  tolerancePct: number;
  /** SIDE entries judged, not stations: a two-band run judges two things per
   *  station line. */
  judged: number;
  pass: number;
  marginal: number;
  fail: number;
  inconclusive: number;
  /**
   * The largest `|deltaPct|` the run reached, and the station line that
   * carried it. Both carry float dust — a corridor that matches its design
   * exactly reports `4.3e-13` at `worstStationK: 0` — so never key a badge or
   * a warning off "there is a worst"; key it off the counts above.
   */
  worstAbsDeltaPct: number | null;
  worstStationK: number | null;
  meanDeltaLeftPct: number | null;
  meanDeltaRightPct: number | null;
}

export interface StationParams {
  stationIntervalM: number;
  swathLeftM: number;
  swathRightM: number;
  stationFromInternalM: number;
  stationToInternalM: number;
  offsetBinM: number;
  minCoveragePct: number;
  effectiveResolutionM: number;
  cellAreaM2: number;
  method: 'RASTER_INTEGRATION';
  densifyStepM: number;
  /** The analysis grid is the alignment corridor bbox, not the full overlap. */
  extent: 'CORRIDOR_BBOX';
  /**
   * What `coveragePct` is measured against. `REFERENCE_SURFACE`: the cells
   * the reference surface covers inside the corridor — the top surface of a
   * surface difference, the design model of a deviation run — so a design
   * narrower than the swath is not "low coverage", and how much of the swath
   * it occupies is reported separately (`swathUsedPct`). `SWATH`: every cell
   * of the swath (runs made before the basis existed, and deviation runs
   * against an IFC reference, which has no XY footprint). Absent on a run
   * older than the field, which is `SWATH` in every reader.
   */
  coverageBasis?: CoverageBasis;
  /** The bands and tolerance the run was made with. Absent on a run that
   *  asked for no check; echoed so the dock's chip tooltip and the re-run
   *  pre-fill can state them without re-reading the job input. */
  crossfall?: CrossfallParams;
}

export type CoverageBasis = 'REFERENCE_SURFACE' | 'SWATH';

export interface StationSummary {
  binCount: number;
  cutVolumeM3: number;
  fillVolumeM3: number;
  netVolumeM3: number;
  cutVolumeAeaM3: number;
  fillVolumeAeaM3: number;
  corridorAreaM2: number;
  meanCoveragePct: number;
  lowCoverageBinCount: number;
  cumulativeMinM3: number;
  cumulativeMaxM3: number;
  /** Internal stations where the cumulative net curve crosses zero. */
  balancePointsInternalM: number[];
  outsideSwathCellCount: number;
  outsideStationRangeCellCount: number;
  /** Reference cells inside the corridor (the coverage denominator, summed). */
  referenceCellCount?: number;
  /**
   * The reference's share of the swath's geometric area (interval length ×
   * swath width), corridor-wide, 0–100. Low means the swath is wider than the
   * surface being measured — information, never a warning.
   */
  swathUsedPct?: number;
  /**
   * How far the reference reaches left / right of the centreline, measured on
   * the cells it covers INSIDE the swath, so it can only ever suggest a
   * narrower swath than the run used. `null` when nothing was covered; absent
   * on runs older than the field.
   */
  referenceExtentLeftM?: number | null;
  referenceExtentRightM?: number | null;
  /** The run-wide crossfall roll-up. `judged` counts SIDE entries, not
   *  stations: a two-band run judges two things per station line. */
  crossfall?: CrossfallSummary;
}

/**
 * The optional per-bin conformance figures `scripts/station_bins.py` adds
 * when the run carried an expectation (Task 6, `exp_bin`; `meanDeviationM`/
 * `crossingPct` added by Task 8's `_FIGURE_KEYS`). Every key is absent —
 * never present-but-null — on a run made with no expectation, so a plain
 * `'meanThicknessM' in bin` (or `!== undefined`) is how a reader asks "did
 * this run have one at all". Once a run HAS one, `meanThicknessM` etc. can
 * still be `null` for a bin that measured nothing (`exp_bin`'s empty-`sel`
 * branch) — absence is a state, never a zero (design doc §4.3).
 */
export interface StationExpectationFigures {
  meanThicknessM?: number | null;
  minThicknessM?: number | null;
  meanDeviationM?: number | null;
  underPct?: number;
  inSpecPct?: number;
  overPct?: number;
  crossingPct?: number;
  verdict?: Verdict;
}

export interface StationBin extends StationExpectationFigures {
  i: number;
  staStartInternalM: number;
  staEndInternalM: number;
  staStartDisplayM: number;
  staEndDisplayM: number;
  cutVolumeM3: number;
  fillVolumeM3: number;
  netVolumeM3: number;
  cumulativeNetM3: number;
  cutAreaM2: number;
  fillAreaM2: number;
  /** null when this bin's local radius is smaller than the swath (R3). */
  cutVolumeAeaM3: number | null;
  fillVolumeAeaM3: number | null;
  cellCount: number;
  binCellCount: number;
  /** Reference cells in this bin — `coveragePct`'s denominator. Equals
   *  `binCellCount` on a `SWATH`-basis run; absent on runs older than the field. */
  refCellCount?: number;
  coveragePct: number;
  /** This bin's reference area over its swath area, 0–100 (see `StationSummary`). */
  swathUsedPct?: number;
  lowCoverage: boolean;
  minRadiusM: number | null;
  minDzM: number | null;
  maxDzM: number | null;
  meanDzM: number | null;
  zTopMeanM: number | null;
  /** A volume over an interval — meaningless on a bin. Present only here,
   *  never on `StationSectionLine` (a station LINE is a plane). */
  deficitVolumeM3?: number;
  /**
   * This bin's START station line's crossfall, carried down onto the bin by
   * the worker (`stack_analysis.py`) so a per-bin reader — the stack station
   * CSV, the dock — needs no second document. The CLOSING line of the last
   * bin has no bin of its own, so the layer's summary counts more sides than
   * this key does, by design. Absent on every run with no check.
   */
  crossfall?: StationCrossfall;
}

export interface StationOffsetsMeta {
  file: string;
  layout: string;
  planes: ['dz', 'zTop', 'zBottom'];
  nStations: number;
  nOffsets: number;
  offsetStartM: number;
  offsetStepM: number;
  nodata: 'NaN';
  byteLength: number;
}

/**
 * The `sections` block of station_volumes.json — the TRUE cross-sections cut
 * at each station LINE, as opposed to `offsetBins`, whose columns are cell
 * means over a station INTERVAL. Absent on runs made before they existed.
 */
export interface StationSectionsMeta {
  file: string;
  layout: string;
  /**
   * Names every plane in the file, in on-disk order. Every run before the
   * multi-layer stack wrote exactly `['zTop', 'zBottom']`; a stack run writes
   * `2N` names. Readers must size the decode off `planes.length`, never off
   * a hardcoded `2` (`scripts/station_bins.py::sample_station_sections`).
   */
  planes: string[];
  nStationLines: number;
  nOffsets: number;
  offsetStartM: number;
  offsetStepM: number;
  nodata: 'NaN';
  byteLength: number;
}

/**
 * One station LINE's true end areas — the figure a cross-section sheet
 * reports. `StationBin.cutAreaM2` is a different quantity (the mean area over
 * the interval) and both are correct answers to different questions.
 */
export interface StationSectionLine extends StationExpectationFigures {
  k: number;
  staInternalM: number;
  staDisplayM: number;
  cutAreaM2: number;
  fillAreaM2: number;
  /** Share of offset columns where BOTH surfaces were measured. */
  coveragePct: number;
  /** False when no column had both surfaces; both areas are then 0. */
  valid: boolean;
  /** Both bands of this station line. A side the run never configured is
   *  `null`; the whole key is absent on a run with no check. */
  crossfall?: StationCrossfall;
}

export interface StationVolumesJson {
  schemaVersion: number;
  /**
   * `"deviation"` on a deviation run; absent (meaning "surface difference")
   * on every surface-difference run, including every one made before this
   * field existed.
   *
   * This is the worker's own field — `scripts/station_bins.py::station_csv`
   * prints the `# Analysis:` line from it — so the dock's CSV download reads
   * it here rather than re-deriving the word from layer metadata, and the two
   * copies of the file provably say the same thing. `StationVolumesLayerMeta.kind`
   * is only the fallback for a bins file written before this field existed.
   */
  analysisKind?: string;
  alignment: StationAlignmentMeta;
  params: StationParams;
  summary: StationSummary;
  offsetBins: StationOffsetsMeta;
  /**
   * Present only on runs that computed true station-line sections. This is
   * THE predicate the whole fallback turns on — `json.stations != null`.
   *
   * These ride as optional additive fields rather than behind a
   * `schemaVersion` bump on purpose: `useStationVolumes` rejects any file
   * newer than the client understands, so a bump would break every dock in
   * the window between deploying the workers and deploying the app, for no
   * gain (design doc §1.6).
   */
  sections?: StationSectionsMeta;
  stations?: StationSectionLine[];
  bins: StationBin[];
  /**
   * CSV-header-only metadata the worker's `compute_station_bins` attaches
   * when the run carried an expectation (Task 16) — the mode/design/
   * tolerances the CSV's `# Expectation:` line names, mirroring
   * `station_bins.py`'s own `exp_meta`. Absent — never present-but-null —
   * on a `NONE` run, exactly like `StationExpectationFigures`'s per-bin
   * keys, so `json.expectation != null` is how a reader asks "did this run
   * have one at all". Not the same block as `StationCorridorExpectation`
   * (`metadata.deviationStats.corridor.expectation` / surface difference's
   * `stats.expectation`) — that one carries the full grid-wide conformance
   * figures; this one carries only what the CSV header needs to print
   * itself.
   */
  expectation?: {
    mode: 'MATCH' | 'CONSTANT' | 'SURFACES';
    /** `null` for SURFACES (no single scalar); a set thickness for
     *  CONSTANT; the MATCH sentinel `0.0` otherwise. */
    thicknessM: number | null;
    underToleranceM: number | null;
    overToleranceM: number | null;
  };
}

/**
 * What lands on `metadata.surfaceDifference.stationVolumes` (~900 bytes).
 * `byproducts.{json,csv,offsets,bins}` are the registered byproduct **filenames**
 * (e.g. `${stem}_stations.json`), not S3 keys — match against
 * `BatchByproduct.filename` (see server T4.2 / rulings T6.1). Any of the
 * four is `null` when that byproduct wasn't produced or registered. `bins`
 * (`station_bins.geojson`, spec §13.7) is absent on layers analysed before
 * the renders addendum — it only decides whether the per-station PDF can
 * offer point-cloud renders; nothing else reads it.
 */
export interface StationVolumesMeta {
  schemaVersion: number;
  alignment: StationAlignmentMeta;
  params: StationParams;
  /**
   * The worker's `summary`, with ONE difference: `balancePointsInternalM` is
   * cut to the first 64 entries here (`balancePointsTruncated: true` marks a
   * cut list). An alternating cut/fill corridor crosses zero once per bin, so
   * the untruncated array is unbounded and this block rides on the `layers`
   * query for every layer at project load.
   *
   * Never read balance points from here — read them from `station_volumes.json`
   * (`StationVolumesJson.summary`, or recompute from `bins` with
   * `aggregate.ts::balancePoints`, which is what the chart does).
   */
  summary: StationSummary & { balancePointsTruncated?: true };
  offsetBins: { nStations: number; nOffsets: number } | null;
  /**
   * Null on a run made before true station-line sections existed. `planes`
   * is optional (not just on a null run) because it rides as an additive
   * field on the same permissive-parse convention as the rest of this
   * block — a layer whose metadata was persisted before this field existed
   * has `sections` but no `planes`; readers fall back to `['zTop', 'zBottom']`.
   */
  sections?: { nStationLines: number; nOffsets: number; planes?: string[] } | null;
  byproducts: {
    json: string | null;
    csv: string | null;
    offsets: string | null;
    bins?: string | null;
    /** `station_sections.f32` — absent/null falls the dock back to the bin means. */
    sections?: string | null;
    /** `station_sections.csv` — one row per station line. */
    sectionsCsv?: string | null;
    /** `<stem>_station_strip.png` — the worker's strip map (strip-map spec §5).
     *  Optional and additive: the DOCK never reads it (it draws its own strip
     *  from the offset planes); only the report preprocess resolves it. */
    strip?: string | null;
  };
}

/** Which analysis produced the block (spec §10.3). */
export type StationVolumesAnalysisKind = 'surfaceDifference' | 'deviation';

/**
 * What lands on `layers.metadata.stationVolumes`. Mirrors
 * `server/background/src/functions/shared/stationVolumesMeta.ts`.
 *
 * The two surface names are not in `station_volumes.json` — they belong to the
 * job, not to the bins — so they ride here. For a deviation run `topLayerName`
 * is the REFERENCE (design) layer and `bottomLayerName` the SOURCE point
 * cloud: the worker feeds the design in as `top`, so `dz = top − bottom` is
 * `−deviation` and positive deviation reads as cut (spec §10.2 step 4).
 *
 * `kind` and the two names are optional so a layer written before this
 * addendum (which only had `metadata.surfaceDifference.stationVolumes`) still
 * type-checks when it is read through the fallback path.
 */
export interface StationVolumesLayerMeta extends StationVolumesMeta {
  kind?: StationVolumesAnalysisKind;
  topLayerName?: string | null;
  bottomLayerName?: string | null;
}

/**
 * The three kinds of series the cross-section chart can draw: the run's own
 * as-built survey, the run's own design planes, and an overlay reference
 * surface (section-overlays spec §3). Bands, hatches and tolerance runs are
 * computed from `asbuilt`/`design` only — `reference` never enters a claim.
 */
export type SeriesRole = 'asbuilt' | 'design' | 'reference';

/**
 * One reference plane in `station_section_overlays.f32`, as the sibling JSON
 * describes it (section-overlays spec §3).
 *
 * `label` is the only name a user ever reads — the server derives it as
 * `<layer> · <surface>` / `<run> · <level>`, because the client has the
 * overlay's ID and nothing else. `ok: false` is a plane the sampler could not
 * produce (a surface outside the corridor, a deleted layer): its bytes are
 * present and all-NaN, and `error` says why in words the reader can act on.
 */
export interface SectionOverlayPlaneMeta {
  id: string;
  label: string;
  role: SeriesRole;
  /**
   * `kind` is `null` for a MALFORMED descriptor: `section_overlays.py` writes
   * `{"kind": None, "layerId": None, "ref": None}` for an entry it could not
   * even read the shape of, and still emits its (all-NaN) plane so the id
   * keeps its place. Narrowing this to the two real kinds made the file's own
   * declared shape unrepresentable.
   */
  source: { kind: 'LAYER_SURFACE' | 'RUN_SECTIONS' | null; layerId: string | null; ref: string | null };
  ok: boolean;
  error: string | null;
}

/**
 * The sidecar JSON of `station_section_overlays.f32`.
 *
 * `lineSource` says what a ROW is: `STATIONS` — one per true station line,
 * the same index the run's own `station_sections.f32` uses — or
 * `BIN_CENTRES`, one per station BIN. The preprocess picks the mode from the
 * RUN's own file, so the chart addresses an overlays row with exactly the
 * `rowIndex` it already computed for the run; there is deliberately no second
 * index to keep in step.
 */
export interface SectionOverlaysJson {
  schemaVersion: number;
  settingsHash: string;
  nStationLines: number;
  nOffsets: number;
  offsetStartM: number;
  offsetStepM: number;
  lineSource: 'STATIONS' | 'BIN_CENTRES';
  planes: SectionOverlayPlaneMeta[];
}

/** The decoded overlays byproduct: one Float32 plane per id, plus its JSON. */
export interface SectionOverlays {
  planes: Record<string, Float32Array>;
  meta: SectionOverlaysJson;
}

/**
 * What lands on `metadata.sectionOverlays`. `byproducts.{f32,json}` are
 * registered byproduct FILENAMES on the run's batch, never S3 keys — the same
 * contract as `StationVolumesMeta.byproducts`.
 *
 * `settingsHash` is the hash of the settings list the file was sampled FOR.
 * When it disagrees with the hash of what the layer is configured with today,
 * the file is stale and the chart says "pending" rather than drawing a line at
 * the wrong elevation.
 */
export interface SectionOverlaysLayerMeta {
  settingsHash: string;
  byproducts: { f32: string; json: string };
  updatedAt: string;
}

/**
 * N Float32 row-major planes, sliced out of station_sections.f32 — N is
 * whatever `meta.sections.planes.length` said, never a hardcoded 2. `zTop`/
 * `zBottom` are kept as plain accessors (views into `planes`, not copies) so
 * every caller written before the stack still works unchanged: a stack file
 * still has these two as its first as-built pair.
 */
export interface StationSections {
  planes: Record<string, Float32Array>;
  planeNames: string[];
  zTop: Float32Array;
  zBottom: Float32Array;
  nStationLines: number;
  nOffsets: number;
  offsetStartM: number;
  offsetStepM: number;
}

/** Three Float32 row-major planes, sliced out of station_offsets.f32. */
export interface StationOffsets {
  dz: Float32Array;
  zTop: Float32Array;
  zBottom: Float32Array;
  nStations: number;
  nOffsets: number;
  offsetStartM: number;
  offsetStepM: number;
}

/**
 * A deviation run's corridor-scoped conformance check (`deviation.py`'s
 * `corridor["expectation"]`, `:1119`) — measured over the swath (`cmask`:
 * populated AND inside the true swath polygon), not the padded bbox grid.
 * Surface difference has no equivalent of this block: its own expectation is
 * grid-wide, over the WHOLE overlap, and lives at `stats.expectation`
 * (Task 13's `ExpectationMeta` in `SurfaceDifferencePanel.tsx`) — a
 * different scope, a different key, on a run that may not even have a
 * corridor at all (station volumes are optional on both analyses).
 *
 * This block is NOT part of `station_volumes.json`'s schema (the file is
 * written to disk before the corridor dict gains this key — see
 * `compute_station_volumes`'s own docstring). It travels as the
 * `station_corridor.json` sidecar, which `processDeviation/worker.py`
 * merges into the layer's own `stats["corridor"]` — reachable on the
 * client as `metadata.deviationStats.corridor` (`postprocess.ts`;
 * `DeviationVolumePanel.tsx` already reads the sibling
 * `metadata.deviationStats.volume` the same way).
 */
export interface StationCorridorExpectation {
  mode: 'MATCH' | 'CONSTANT' | 'SURFACES';
  /** `null` for SURFACES (no single scalar); a set thickness for CONSTANT;
   *  the MATCH sentinel `0.0` otherwise. */
  thicknessM: number | null;
  underToleranceM: number | null;
  overToleranceM: number | null;
  failAreaPct: number;
  /** Of the CORRIDOR (`100 - coveragePct` on the sibling totals) — a
   *  different denominator from `ExpectationMeta.notMeasuredAreaPct`
   *  (whole grid). Do not conflate the two. */
  notMeasuredAreaPct: number;
  measuredCells: number;
  meanThicknessM: number | null;
  minThicknessM: number | null;
  maxThicknessM: number | null;
  meanDeviationM: number | null;
  stdDeviationM: number | null;
  underAreaM2: number;
  deficitVolumeM3: number;
  surplusVolumeM3: number;
  crossingCellCount: number;
  crossingPct: number;
  underPct: number;
  inSpecPct: number;
  overPct: number;
  criteria: Criterion[];
  verdict: Verdict;
}

/** The corridor totals a deviation run's `stats["corridor"]` carries
 *  (`deviation.py:1072`), same shape as surface difference's own
 *  `stats["corridor"]` — plus `expectation`, which only deviation's ever
 *  carries (see `StationCorridorExpectation`). */
export interface StationCorridorMeta {
  cutVolumeM3: number;
  fillVolumeM3: number;
  netVolumeM3: number;
  areaM2: number;
  cellCount: number;
  coveragePct: number;
  coverageBasis?: CoverageBasis;
  swathUsedPct?: number;
  expectation?: StationCorridorExpectation;
}

// ---------------------------------------------------------------------------
// The multi-layer STACK result document (`scripts/stack_analysis.py ::
// analyse_stack`). A LEVEL is a surface; a LAYER is the material between two
// adjacent levels. N levels describe N-1 layers — never use "layer" for a
// surface, the arithmetic depends on the distinction.
// ---------------------------------------------------------------------------

/** A level either has an as-built surface or it does not. */
export type StackLevelState = 'SURVEYED' | 'PENDING';

/**
 * A layer is MEASURABLE only when BOTH bounding levels were surveyed — a
 * thickness needs two surfaces, so one missing as-built makes both of its
 * adjacent layers PENDING (`layer_stack.py::layer_state`).
 */
export type StackLayerState = 'MEASURABLE' | 'PENDING';

/**
 * A verdict, or the absence of one. `PENDING` is NOT a severity: it is
 * excluded from a roll-up entirely rather than counted as a pass (global
 * constraint, "absence is a state, never a zero").
 */
export type StackVerdict = Verdict | 'PENDING';

export interface StackLevel {
  name: string;
  state: StackLevelState;
  /**
   * The tolerances this level's LAYER was judged against, in metres —
   * `stack_analysis.py`'s `_judge(actual, design, levels[a].underTolM,
   * levels[a].overTolM, 'SURFACES')`, where `a` is the layer's TOP level.
   *
   * Echoed onto the result document (and from there onto layer metadata)
   * because they exist nowhere else this side of the run input: the stacked
   * cross-section classifies each band itself and has nothing to compare a
   * column against without them.
   *
   * `null`/absent on a run made before that echo — no per-layer marking, never
   * a zero tolerance, which would mark every column out of spec.
   */
  underTolM?: number | null;
  overTolM?: number | null;
}

/**
 * One `expectation.evaluate` answer, as `stack_analysis.py::_judge` stores it.
 * Ruling R11 froze the key set: a block that measured nothing carries the SAME
 * keys as one that did, with `measuredCells: 0` and real zeros in the volume
 * and area fields — which is exactly why both CSV writers gate their
 * measurement columns on `measuredCells`, not on the layer's state.
 */
export interface StackBlock {
  measuredCells: number;
  meanThicknessM: number | null;
  minThicknessM: number | null;
  maxThicknessM: number | null;
  meanDeviationM: number | null;
  stdDeviationM: number | null;
  underAreaM2: number;
  deficitVolumeM3: number;
  surplusVolumeM3: number;
  crossingCellCount: number;
  crossingPct: number;
  underPct: number;
  inSpecPct: number;
  overPct: number;
  /** The grid coverage % is recovered from the `coverage` criterion — the
   *  block deliberately has no field of its own for it (`_coverage_pct`). */
  criteria: Criterion[];
  verdict: Verdict;
}

export interface StackLayer {
  i: number;
  /** `"<levelFrom> → <levelTo>"`, built by the worker. Free text: it carries
   *  user-typed level names and must be CSV-quoted. */
  name: string;
  levelFrom: string;
  levelTo: string;
  state: StackLayerState;
  verdict: StackVerdict;
  /** Absent on a PENDING layer — the layer exists but nothing was measured. */
  thickness?: StackBlock;
  elevationTop?: StackBlock;
  elevationBottom?: StackBlock;
  /** Absent on a PENDING layer, which therefore contributes no station rows. */
  stations?: StationBin[];
  thicknessTif?: string;
  /**
   * This layer's crossfall roll-up, over its TOP level's `(as-built, design)`
   * pair — never the two as-built levels the thickness is measured between,
   * which would report the thickness's own slope as a crossfall.
   *
   * The KEY's presence is what `stack_stations_csv` asks to decide whether the
   * station CSV carries the eight crossfall columns, so it is a statement
   * about the RUN, not about any one bin.
   */
  crossfall?: CrossfallSummary;
}

export interface StackGrid {
  /** `[minX, minY, maxX, maxY]` — the UNION over every declared surface. */
  bounds: [number, number, number, number];
  width: number;
  height: number;
  effectiveResolutionM: number;
  requestedResolutionM: number;
}

export interface StackResult {
  levels: StackLevel[];
  layers: StackLayer[];
  /** Worst severity across the stack, PENDING when nothing is built yet. */
  rollUp: StackVerdict;
  /** Absent on a run that never built a grid (every level unsurveyed). */
  grid?: StackGrid;
  /**
   * The run-level echo of what was ASKED for. Written by
   * `stack_analysis.py::analyse_stack` at the same address
   * `station_bins.py` uses on a single-pair run
   * (`result["params"]["crossfall"]`), so one client reader serves both
   * documents.
   *
   * Today it carries the crossfall BANDS and nothing else, because the bands
   * are the one thing no other channel delivers: they ride in
   * `station_input.json` on the S3 side of the ECS payload boundary, and the
   * scalar echo in `inputJson` (capped at 8192 characters) leaves them out.
   * `layers[i].crossfall` is a SUMMARY and has no bands at all.
   *
   * Absent on a run with no check, and on every stack written before the echo
   * existed — `dockModel.ts` falls back to reconstructing a band from a
   * fitted line's endpoints for those.
   */
  params?: { crossfall?: CrossfallParams | null } | null;
  /** The 2N-plane cross-section byproduct; same layout as the single-pair one. */
  sections?: StationSectionsMeta;
}
